import * as THREE from 'three';
import { applyInteriorDark } from '../engine/interior';
import { DEPLOY_HP } from '../config';
import { Inventory, type Stack } from './inventory';
import { buildCraftableModel } from './craftables';
import { LOCAL_PLAYER } from './identity';
import type { Box, BoxPool } from '../engine/collision';
import { nearestOnRay } from './aim';
import { preloadGLB, makeGLBBuildable } from '../engine/gltf';

// Owner-modelled GLBs (2026-07-09), same instant-placeholder-then-swap pattern as every buildable in
// craftables.ts: buildChestMesh (below) is the procedural fallback shown while the real chest loads.
const CHEST_SMALL_FILE = 'chest_small.glb', CHEST_LARGE_FILE = 'chest_large.glb';
preloadGLB('/models/' + CHEST_SMALL_FILE);
preloadGLB('/models/' + CHEST_LARGE_FILE);

// Deployable storage chests. A small chest opens a 6x2 grid; a normal chest opens a 10x4 grid. Each
// chest owns a count-based Inventory (the same model corpses and the Base Claim use) so the storage
// screen treats them all identically: YOUR inventory on the right, the chest on the left. Client-only
// for now (no persistence), exactly like corpses and loot containers.

export type ChestKind = 'small' | 'big';

// Chests scale up ~15% with the rest of the hero deployables (owner: models "feel a tad too small").
// Colliders and the lock anchor derive from the same factor so they keep hugging the mesh; the FOOT
// ghost half-extents in deployables.ts carry it too.
export const CHEST_SCALE = 1.15;
function chestScale(kind: ChestKind): number { return (kind === 'big' ? 1.0 : 0.66) * CHEST_SCALE; }

const DIMS: Record<ChestKind, { cols: number; rows: number; title: string }> = {
  small: { cols: 6, rows: 2, title: 'Small Chest' },
  big: { cols: 10, rows: 4, title: 'Large Chest' },
};
// Collision half-extents [hx,hy,hz] per kind -- MEASURED off the real fitted GLB (Box3), same convention
// as deployables.ts's FOOT table. Re-measure if the owner re-scales the chest files in buildable_resizers.json.
const CHEST_FOOT: Record<ChestKind, [number, number, number]> = { small: [0.42, 0.22, 0.36], big: [0.81, 0.36, 0.55] };

export class Chest {
  readonly group = new THREE.Group();
  readonly pos: THREE.Vector3;
  readonly contents: Inventory;
  readonly kind: ChestKind;
  lock: 'wooden_lock' | 'keypad_lock' | null = null;
  label: string | null = null;      // custom name (owner QoL): shows on aim + as the open-panel title
  code: string | null = null;       // keypad combo (if a keypad_lock is on it); null = wooden/no code
  granted: string[] = [];           // players who entered the code -> keep access even off the base claim (#12)
  owner = LOCAL_PLAYER; // who placed it -- a locked chest only opens for its owner (MP-forward; always you in SP)
  hp: number; maxHp: number;        // chests are destructible (#13): raid them down, contents spill into a 10-min satchel

  constructor(kind: ChestKind, pos: THREE.Vector3, yaw: number) {
    this.kind = kind;
    // Rust-matched HP (owner 2026-07-12): small wood box 150, large 300 (was a flat 500 -- chests are
    // soft raid targets, walls are the hard shell). Reads DEPLOY_HP so F4 tuning covers chests too.
    this.hp = this.maxHp = DEPLOY_HP[kind === 'big' ? 'bigchest' : 'chest'] ?? 200;
    this.contents = new Inventory(DIMS[kind].cols * DIMS[kind].rows); // slot capacity = the visible grid
    this.pos = pos.clone();
    const file = kind === 'big' ? CHEST_LARGE_FILE : CHEST_SMALL_FILE;
    this.group.add(makeGLBBuildable(file, 1, (g) => buildChestMesh(g, kind)));
    this.group.position.copy(this.pos);
    this.group.rotation.y = yaw;
    this.group.traverse((o) => { o.userData.chest = this; }); // backref so a cursor/crosshair ray resolves this chest
  }

  get cols(): number { return DIMS[this.kind].cols; }
  get rows(): number { return DIMS[this.kind].rows; }
  get title(): string { return this.label ?? DIMS[this.kind].title; }

  // Attach a lock model by the clasp (front), once. Anchor MEASURED off the real fitted GLB per kind
  // (Box3, same convention as deployables.ts's FOOT table) -- re-measure if the owner re-scales
  // chest_small.glb/chest_large.glb in buildable_resizers.json.
  attachLock(kind: 'wooden_lock' | 'keypad_lock'): void {
    const [x, y, z] = LOCK_ANCHOR[this.kind];
    const lk = buildCraftableModel(kind); lk.scale.setScalar(0.7);
    lk.position.set(x, y, z);
    lk.traverse((o) => { o.castShadow = true; });
    this.group.add(lk); this.lock = kind;
  }
}
// Lock-clasp anchor [x,y,z] in the chest group's own local space (front face, near the top) -- MEASURED,
// not derived from chestScale (that formula only fits the procedural placeholder's box proportions).
const LOCK_ANCHOR: Record<ChestKind, [number, number, number]> = { small: [0, 0.24, 0.38], big: [0, 0.39, 0.57] };

export class ChestManager {
  readonly group = new THREE.Group();
  private readonly list: Chest[] = [];
  private readonly cols: Array<{ x: number; y: number; z: number; yaw: number; hx: number; hy: number; hz: number }> = []; // parallel to list
  // MP: fired when the LOCAL player locks a chest, so NetBackend broadcasts + persists it (the lock VISUAL projects to
  // everyone + late joiners, and a WOODEN lock lets the DO enforce access). No-op in single-player.
  onLock: (x: number, y: number, z: number, kind: 'wooden_lock' | 'keypad_lock', code?: string) => void = () => {};

  // `owner` = who placed it (a locked chest opens for its owner + their claim members). Defaults to
  // LOCAL_PLAYER for a local placement; the network replay passes the true actor so a remote-placed chest
  // is owned by its real placer on every client (not by whoever replayed it).
  deploy(kind: ChestKind, pos: THREE.Vector3, yaw: number, owner: string = LOCAL_PLAYER): Chest {
    const c = new Chest(kind, pos, yaw);
    c.owner = owner;
    this.list.push(c);
    this.group.add(c.group);
    // A chest is a real obstacle (collider so you can't walk through it) -- half-extents MEASURED off the
    // real fitted GLB per kind, same convention as deployables.ts's FOOT table.
    const [hx, hy, hz] = CHEST_FOOT[kind];
    this.cols.push({ x: pos.x, y: pos.y, z: pos.z, yaw, hx, hy, hz });
    return c;
  }

  // Append each chest's footprint to the world collider set (call from collectWorldBoxes each frame).
  collectColliders(out: Box[], pool: BoxPool): void {
    for (const p of this.cols) out.push(pool.take(p.x, p.y + p.hy, p.z, p.hx, p.hy, p.hz, p.yaw));
  }

  // Closest chest within horizontal range of the point (for the E interact prompt), or null.
  nearest(p: THREE.Vector3, range: number): Chest | null {
    let best: Chest | null = null;
    let bestSq = range * range;
    for (const c of this.list) {
      const dx = c.pos.x - p.x, dz = c.pos.z - p.z, sq = dx * dx + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = c; }
    }
    return best;
  }

  // The chest the cursor/crosshair is aimed at (precise mesh hit, then forgiving ray-proximity for small chests), or null.
  aimed(raycaster: THREE.Raycaster): Chest | null {
    for (const h of raycaster.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.chest) return o.userData.chest as Chest; o = o.parent; }
    }
    return nearestOnRay(raycaster, this.list.map((c) => ({ x: c.pos.x, y: c.pos.y + 0.3, z: c.pos.z, rec: c })), 0.45);
  }

  // Attach a lock to the nearest unlocked chest in range. Returns true if one took it (consume the lock item).
  // A keypad passes its owner-set `code`; the owner is auto-granted so they never have to type it.
  lockNearest(p: THREE.Vector3, range: number, kind: 'wooden_lock' | 'keypad_lock', code?: string): boolean {
    const c = this.nearest(p, range);
    if (!c || c.lock) return false;
    c.attachLock(kind);
    if (kind === 'keypad_lock' && code) { c.code = code; c.granted = [c.owner]; }
    this.onLock(c.pos.x, c.pos.y, c.pos.z, kind, code); // MP: project the lock (+ let the DO enforce a wooden lock)
    return true;
  }

  // REMOTE APPLY (MP): attach the lock another player placed on the chest at (x,y,z), owned by the real actor, so the
  // lock renders for everyone + late joiners. Idempotent (skips a chest that already has a lock).
  applyLockAt(x: number, y: number, z: number, kind: 'wooden_lock' | 'keypad_lock', owner: string, code?: string): void {
    const c = this.nearest(new THREE.Vector3(x, y, z), 0.6);
    if (!c || c.lock) return;
    c.owner = owner;
    c.attachLock(kind);
    if (kind === 'keypad_lock' && code) { c.code = code; c.granted = [owner]; }
  }
  // Rename the chest at (x,y,z) -- local apply for BOTH the SP intent and the MP chestNameAt op.
  renameAt(x: number, y: number, z: number, name: string): void {
    const c = this.nearest(new THREE.Vector3(x, y, z), 0.6);
    if (c) c.label = name.trim().slice(0, 24) || null;
  }

  // The nearest chest to a point (for keypad code-set / code-entry), or null.
  nearestChest(p: THREE.Vector3, range: number): Chest | null { return this.nearest(p, range); }

  // A remote player entered this keypad chest's correct code (containerGrantAt): record them locally so this
  // client's lock gate agrees with the server's durable grant set.
  grantAt(x: number, y: number, z: number, user: string): void {
    const c = this.nearest(new THREE.Vector3(x, y, z), 0.6);
    if (c && !c.granted.includes(user)) c.granted.push(user);
  }

  // Damage the chest nearest a hit point (a raid tool -- explosive/melee). On destroy, hand its contents to `drop`
  // (main spawns a 10-min satchel) and remove it. Returns true if a chest was hit. NOTE (M1): the chest STRUCTURE
  // + its local contents are client-side, so in multiplayer this resolves on the attacker's client; a fully-synced
  // server-authoritative raid is M2. In single-player it is exact.
  damageAt(x: number, y: number, z: number, dmg: number, drop: (items: Array<{ id: string; n: number }>, dx: number, dz: number) => void): boolean {
    let best: Chest | null = null, bestSq = 1.6 * 1.6;
    for (const c of this.list) { const dx = c.pos.x - x, dy = c.pos.y + 0.3 - y, dz = c.pos.z - z, sq = dx * dx + dy * dy + dz * dz; if (sq < bestSq) { bestSq = sq; best = c; } }
    if (!best) return false;
    best.hp -= dmg;
    if (best.hp <= 0) {
      const items: Array<{ id: string; n: number }> = [];
      for (const st of best.contents.serializeSlots()) if (st) items.push({ id: st.id, n: st.n });
      if (items.length) drop(items, best.pos.x, best.pos.z);
      this.remove(best);
    }
    return true;
  }

  // Hammer PICK-UP (owner 2026-07-12): an EMPTY, UNLOCKED chest returns to the inventory as its item.
  // Deny reason for the HUD, or null = allowed.
  pickupDeny(c: Chest): string | null {
    if (c.lock) return 'Remove the lock first';
    if (c.contents.entries().length) return 'Must be empty';
    return null;
  }
  pickupAt(x: number, y: number, z: number): 'chest' | 'bigchest' | null {
    const c = this.nearestChest(new THREE.Vector3(x, y, z), 0.9);
    if (!c || this.pickupDeny(c)) return null;
    const kind = c.kind === 'big' ? 'bigchest' : 'chest';
    this.remove(c);
    return kind;
  }

  remove(c: Chest): void {
    const i = this.list.indexOf(c);
    if (i < 0) return;
    this.list.splice(i, 1);
    this.cols.splice(i, 1);
    this.group.remove(c.group);
  }

  // --- LOCAL WORLD SAVE (game/persist.ts): replay deploy() per chest so mesh/collider/backref
  // regenerate, then restore lock/owner/contents onto the returned record. ---
  serialize(): SavedChest[] {
    return this.list.map((c) => ({
      kind: c.kind, x: c.pos.x, y: c.pos.y, z: c.pos.z, yaw: c.group.rotation.y,
      lock: c.lock, code: c.code, owner: c.owner, label: c.label, contents: c.contents.serializeSlots(),
    }));
  }
  deserialize(data: SavedChest[]): void {
    for (const s of data) {
      const c = this.deploy(s.kind === 'big' ? 'big' : 'small', new THREE.Vector3(s.x, s.y, s.z), s.yaw);
      c.owner = s.owner;
      if (s.label) c.label = s.label;
      if (s.lock) { c.attachLock(s.lock); if (s.code) { c.code = s.code; c.granted = [c.owner]; } }
      c.contents.deserializeSlots(s.contents);
    }
  }
}

// Local world save shape for one chest.
export interface SavedChest {
  kind: ChestKind; x: number; y: number; z: number; yaw: number;
  lock: 'wooden_lock' | 'keypad_lock' | null; code?: string | null; owner: string; label?: string | null; contents: Array<Stack | null>;
}

export function buildChestMesh(g: THREE.Group, kind: ChestKind): void {
  const s = chestScale(kind); // overall scale (includes the hero-deployable 1.15 bump)
  const w = 0.8 * s, h = 0.5 * s, d = 0.5 * s;
  const wood = new THREE.MeshLambertMaterial({ color: 0x7a5230, flatShading: true });
  const lid = new THREE.MeshLambertMaterial({ color: 0x8a5f37, flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: 0x5f4827, flatShading: true });
  const iron = new THREE.MeshLambertMaterial({ color: 0x3a3f45, flatShading: true });
  for (const m of [wood, lid, dark, iron]) applyInteriorDark(m); // chests dim inside a sealed base like every prop
  const add = (mesh: THREE.Mesh): THREE.Mesh => { mesh.castShadow = true; mesh.receiveShadow = true; g.add(mesh); return mesh; };

  const body = add(new THREE.Mesh(new THREE.BoxGeometry(w, h * 0.66, d), wood));
  body.position.y = h * 0.33;

  // A rounded-ish lid: a slightly tapered box sitting on top.
  const top = add(new THREE.Mesh(new THREE.BoxGeometry(w, h * 0.34, d), lid));
  top.position.y = h * 0.66 + h * 0.17;

  // Iron bands + a clasp, just for read.
  const band = add(new THREE.Mesh(new THREE.BoxGeometry(w * 1.02, h * 0.1, d * 1.02), iron));
  band.position.y = h * 0.5;
  const clasp = add(new THREE.Mesh(new THREE.BoxGeometry(w * 0.14, h * 0.22, d * 0.06), iron));
  clasp.position.set(0, h * 0.5, d / 2);
  // Rustic greebles: vertical iron straps wrapping body + lid, rope side handles, a worn lid-edge strip.
  for (const sx of [-1, 1]) {
    const strap = add(new THREE.Mesh(new THREE.BoxGeometry(w * 0.09, h * 1.02, d * 1.04), iron));
    strap.position.set(sx * w * 0.3, h * 0.51, 0);
    const handle = add(new THREE.Mesh(new THREE.BoxGeometry(w * 0.06, h * 0.1, d * 0.5), dark));
    handle.position.set(sx * (w / 2 + w * 0.02), h * 0.4, 0);
  }
  const wear = add(new THREE.Mesh(new THREE.BoxGeometry(w * 0.96, h * 0.05, d * 0.04), dark));
  wear.position.set(0, h * 0.68, d / 2); // scuffed rim where the lid meets the body
}
